import React, { FC, useEffect, useState } from 'react'

import { Grid, Theme, useMediaQuery, useTheme } from '@mui/material'
import { isEmpty, isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'

import { LevelOfStressWidget } from '../../../components/ReportsWidgets'
import { PopularityWidget } from '../../../components/ReportsWidgets/PopularityWidget'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { getNormalizedBarData } from '../../../utils'
import { getJournalingOptionTranslationKey } from '../../TradingLog/tradingLogLabels'
import { fetchStrategyReport } from '../ReportsSlice'

import { SQNWidget } from './SQNWidget'

export const TradingStrategyReports: FC = () => {
  const { palette } = useTheme()
  const { t } = useTranslation()
  const matchesBreakpoints: boolean = useMediaQuery((theme: Theme) => theme.breakpoints.up(2000))
  const dispatch = useAppDispatch()

  const { period, currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)
  const { strategy } = useAppSelector(({ reports }) => reports)
  const { mostPopularProfitable, topStrategyByLevelOfStress, topBySystemQualityNumber } = strategy
  const [displayWidth, setDisplayWidth] = useState(window.innerWidth)

  useEffect(() => {
    const periodsIsEqual = strategy?.meta?.period && period && isEqual(strategy.meta.period, period)
    const isNeedToUpdate =
      isEmpty(strategy) || !periodsIsEqual || currentAccount?.id !== strategy?.meta.account

    if (currentAccount?.id && isNeedToUpdate) {
      dispatch(
        fetchStrategyReport({
          account: currentAccount?.id,
          period: period,
        }),
      )
    }
  }, [period, currentAccount])

  const popularStrategiesData = getNormalizedBarData<'strategy'>(
    mostPopularProfitable,
    'strategy',
    'amount',
    10,
  ).map((item) => ({
    ...item,
    parameter: t(
      `tradingLogPage.journaling.options.${getJournalingOptionTranslationKey(
        item.parameter,
        'tradingStrategy',
      )}`,
      item.parameter,
    ),
  }))
  const profitableStrategiesData = getNormalizedBarData<'strategy'>(
    mostPopularProfitable,
    'strategy',
    'profit',
    10,
  )
    .map((item) => ({
      ...item,
      parameter: t(
        `tradingLogPage.journaling.options.${getJournalingOptionTranslationKey(
          item.parameter,
          'tradingStrategy',
        )}`,
        item.parameter,
      ),
    }))
    .filter((bar) => bar.value >= 0)
  const stressStrategiesData = getNormalizedBarData<'strategy'>(
    topStrategyByLevelOfStress,
    'strategy',
    'perCent',
    10,
  ).map((item) => ({
    ...item,
    parameter: t(
      `tradingLogPage.journaling.options.${getJournalingOptionTranslationKey(
        item.parameter,
        'tradingStrategy',
      )}`,
      item.parameter,
    ),
  }))

  const localizedSqnData = topBySystemQualityNumber?.map((item) => ({
    ...item,
    symbol: t(
      `tradingLogPage.journaling.options.${getJournalingOptionTranslationKey(
        item.symbol,
        'tradingStrategy',
      )}`,
      item.symbol,
    ),
  }))

  useEffect(() => {
    const handleResize = () => {
      setDisplayWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <Grid container spacing={2}>
      <Grid
        item
        lg={matchesBreakpoints ? 4 : 6}
        xs={12}
        sm={12}
        md={6}
        sx={{ pageBreakInside: 'avoid' }}
      >
        <PopularityWidget
          title={t('mostPopularTradingStrategy', 'Most popular trading strategy')}
          explanation={t(
            'theGraphRepresentsWhatAreTheMostPopularStrategyAreSelected',
            'The graph represents what trading strategies are selected frequently',
          )}
          data={popularStrategiesData}
          mainColor={palette.blue[500] as string}
          strategies
        />
      </Grid>
      <Grid
        item
        lg={matchesBreakpoints ? 4 : 6}
        xs={12}
        sm={12}
        md={6}
        sx={{ pageBreakInside: 'avoid' }}
      >
        <PopularityWidget
          title={t('mostProfitableTradingStrategy', 'Most profitable trading strategy')}
          explanation={t(
            'theGraphRepresentsWhatAreTheMostProfitableStrategyHaveMostProfit',
            'The graph represents what trading strategies have the most Net profit',
          )}
          data={profitableStrategiesData}
          mainColor={palette.green[400] as string}
          strategies
          currency={currentAccount?.currency}
        />
      </Grid>
      <Grid
        item
        lg={matchesBreakpoints ? 4 : 6}
        xs={12}
        sm={12}
        md={6}
        sx={{ pageBreakInside: 'avoid' }}
      >
        <LevelOfStressWidget
          title={t('topStrategyByLevelOfStress', 'Top strategy by level of stress')}
          explanation={t(
            'theGraphRepresentsWhatStrategiesTheMostStressfulStrategy',
            'The graph represents what trading strategies are the most stressful',
          )}
          data={stressStrategiesData}
          minZone={30}
          maxZone={60}
          strategies
        />
      </Grid>

      <Grid
        item
        lg={matchesBreakpoints ? 4 : 6}
        xs={12}
        sm={12}
        md={6}
        sx={{ pageBreakInside: 'avoid' }}
      >
        <SQNWidget sqnData={localizedSqnData} displayWidth={displayWidth} />
      </Grid>
    </Grid>
  )
}
